/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Written by Alexander Agudelo < alex.agudelo@kemu.com >, 2020
 * Date: 06/Dec/2020
 * Last Modified: 13/08/2025, 2:44:51 pm
 * Modified By: Alexander Agudelo
 * Description:  State of gates might happen outside the store (Kemu-core), this cook
 * uses Kemu's event listener subscriptions to notify the component the state has changed
 * 
 * ------
 * Copyright (C) 2020 Kemu - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { widgetStateChanged } from '@src/features/uiSignals/actions';
import KemuCore from '@kemu-io/kemu-core';
import { WidgetState, WidgetStateChangeEvent } from '@kemu-io/kemu-core/types';
import { extractPrivateProperties, deepMerge } from '@kemu-io/kemu-core/common/utils';
import { safeJsonClone, safeJSONStringify } from '../utils';
import useWidgetState from './useWidgetState';

export type GetStateFunction<T> = ((currentState: Readonly<T>) => T | Promise<T>);
export type NewStateOrFunction<T extends Record<string, any>> = (Readonly<T>) | GetStateFunction<T>;
type GateStateResponse<T extends Record<string, any> = any> = [
	currentState: T,
	setState: (newState: NewStateOrFunction<T>, triggerEvents?: boolean) => Promise<void>,
	forceSetState: (newState: WidgetState<T>) => void,
];

type CompareFunction<T extends Record<string, any> = any> = (prevState: WidgetState<T>, newState: WidgetState<T>) => boolean;

const getStateCopy = <T extends Record<string, any> = any>(currentState: WidgetState<T>): T => {
	const privateLessState = safeJsonClone(currentState);
	const privatePropsOnly = extractPrivateProperties(currentState);
	const merged = deepMerge<WidgetState<T>>(privateLessState, privatePropsOnly);
	return merged;
};

/**
 * Keeps track of the state of the widget
 * @param recipePoolId the id of the recipe in the pool
 * @param thingId the id of the thing in the recipe (blockRecipeId)
 * @param widgetId the id of the widget in the thing.
 * @param compareFun a function to call when a new state is available. The function
 * should return true if the new state needs to be updated, otherwise the new state
 * will be ignored. 
 * 
 * IMPORTANT: Make sure this function is declared OUTSIDE the component or created with a 
 * useCallback. 
 */
function useReactiveWidgetState<T extends Record<string, any> = any> (
	recipePoolId: string,
	thingId: string,
	widgetId: string,
	compareFun?: CompareFunction<T>
): GateStateResponse<T> {
  const dispatch = useDispatch();
	const stateRef = useRef<(WidgetState<T>) | null>(null);
	const { getState } = useWidgetState<WidgetState<T>>(recipePoolId, thingId, widgetId);

	// Read state directly from the recipe during initialization
	const [state, setState] = useState<WidgetState<T>>(() => {
		const recipeState = getState();
		// WARNING: nested objects will be copied by reference, causing the state to mutate
		// if the widget state is modified. Widget components MUST make sure never to mutate
		// properties directly.
		stateRef.current = {
			...(recipeState as WidgetState<T>),
		};

		return { ...recipeState } as WidgetState<T>;
	});

	/**
	 * Updates the state of the widget without comparing the previous state or checking
	 * the compare function. This is useful when the widget needs to be updated regardless.
	 */
	const forceSetState = useCallback((newState: WidgetState<T>) => {
		stateRef.current = getStateCopy<WidgetState<T>>(newState);
		KemuCore.setGateState(recipePoolId, thingId, widgetId, stateRef.current, true);
	}, [recipePoolId, thingId, widgetId]);

	const onGateStateChanged = useCallback((evt: WidgetStateChangeEvent<T>) => {
		if (evt.recipeId === recipePoolId && evt.gateId === widgetId) {
			if (compareFun && stateRef.current) {
				const shouldUpdate = compareFun(stateRef.current, evt.newState);
				if (shouldUpdate) {
					// NOTE: here we need to keep a copy of the widget state in memory
					// so we can compare it with new states and determine if it has changed.
					// However, this copy cannot be the same reference since it will then be 
					// referring to the same object in memory and any changes will be reflected immediately,
					// (making the comparison later useless). We can't just stringify and parse because
					// some widgets will have private properties that are very large or have circular references.
					// So we need to extract the private properties before stringifying the state, then
					// put back the private properties (since they will be ignored anyway during the comparison)
					// const privateLessState = safeJsonClone(evt.newState);
					// const privatePropsOnly = extractPrivateProperties(evt.newState);
					// const merged = deepMerge<WidgetState<T>>(privateLessState, privatePropsOnly);
					// stateRef.current = merged;
					stateRef.current = getStateCopy<WidgetState<T>>(evt.newState as WidgetState<T>);

					setState(evt.newState);
				}
			} else {
				// Compare state without private properties
				const prevStr = safeJSONStringify(stateRef.current, true);
				const newStr = safeJSONStringify(evt.newState, true);
				if (prevStr !== newStr) {
					stateRef.current = getStateCopy<WidgetState<T>>(evt.newState as WidgetState<T>);

					setState(evt.newState);
				}
			}
		}
	}, [recipePoolId, widgetId, compareFun]);


	const setGateState = useCallback(async (newState: T | NewStateOrFunction<T>, triggerEvents=true): Promise<void> => {
    // Emits state update for history tracking
		const emitStateChanged = () => {
      if (triggerEvents) {
        dispatch(widgetStateChanged({ recipeId: recipePoolId, thingId, widgetId }));
      }
    };

		if (typeof newState === 'function') {
			// Read the latest state directly from the recipe
			const latestState = getState();
			// Call caller to read the next-to-be state
			const updatedState = await (newState as GetStateFunction<T>)(latestState as WidgetState<T>);
			// Ignore updates that contain the same object
			if (updatedState !== latestState) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				KemuCore.setGateState(recipePoolId, thingId, widgetId, updatedState as any, triggerEvents);
        emitStateChanged();
			}
		} else {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			KemuCore.setGateState(recipePoolId, thingId, widgetId, newState as any, triggerEvents);
      emitStateChanged();
		}
  }, [recipePoolId, thingId, widgetId, getState, dispatch]);


	useEffect(() => {
		KemuCore.onGateStateChanged(onGateStateChanged, thingId, widgetId);

		// Update the state if one of the key properties changes (because it means)
		// we are not longer referring to the same widget.
		const latestState = getState();
		// Do a double check to prevent unnecessary render cycles by setting a state that is effectively the same
		const differ = safeJSONStringify(latestState, true) !== safeJSONStringify(stateRef.current, true);
		if (differ) {
			stateRef.current = getStateCopy<WidgetState<T>>(latestState as WidgetState<T>);

			setState(latestState);
		}


		return () => {
			KemuCore.offGateStateChanged(onGateStateChanged, thingId, widgetId);
		};
	}, [recipePoolId, thingId, widgetId, onGateStateChanged, getState]);

	return [
		state,
		setGateState,
		forceSetState,
	];
}

export default useReactiveWidgetState;
